// maths.portll.net — preset avatar picker (NEVER a photo upload for a child).
//
// A CLOSED set of ~12 emoji-backed preset ids. The stored value is a stable string id
// (e.g. "koala-01"); the emoji is only how we DRAW it, so we can restyle later without
// migrating data. Deliberately no file/photo upload — a child's likeness never leaves
// the device, and the cloud child doc stays non-PII. Fully controlled: value/onChange.

import { injectStyles } from "./ui.jsx";

/** The preset roster. Warm + Australian-leaning; each emoji is visually distinct. */
export const AVATARS = [
  { id: "koala-01", emoji: "🐨", label: "Koala" },
  { id: "kangaroo-01", emoji: "🦘", label: "Kangaroo" },
  { id: "echidna-01", emoji: "🦔", label: "Echidna" },
  { id: "platypus-01", emoji: "🦫", label: "Platypus" },
  { id: "cockatoo-01", emoji: "🦜", label: "Cockatoo" },
  { id: "kookaburra-01", emoji: "🐦", label: "Kookaburra" },
  { id: "turtle-01", emoji: "🐢", label: "Turtle" },
  { id: "frog-01", emoji: "🐸", label: "Frog" },
  { id: "octopus-01", emoji: "🐙", label: "Octopus" },
  { id: "crab-01", emoji: "🦀", label: "Crab" },
  { id: "butterfly-01", emoji: "🦋", label: "Butterfly" },
  { id: "dolphin-01", emoji: "🐬", label: "Dolphin" },
];

const BY_ID = Object.fromEntries(AVATARS.map((a) => [a.id, a]));

/** Emoji for a stored avatar id (falls back to a friendly sprout for unknown ids). */
export function avatarEmoji(id) {
  return (BY_ID[id] && BY_ID[id].emoji) || "🌱";
}

/** Small read-only avatar bubble for lists/headers. */
export function Avatar({ id, size = 40, className = "" }) {
  const a = BY_ID[id];
  return (
    <span
      className={("avatar-bubble " + className).trim()}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.6) }}
      role="img"
      aria-label={a ? a.label : "Avatar"}
    >
      {avatarEmoji(id)}
    </span>
  );
}

/** Controlled grid picker. `value` is an avatar id; `onChange(id)` on selection. */
export default function AvatarPicker({ value, onChange, label = "Choose an avatar" }) {
  return (
    <div className="avpick" role="radiogroup" aria-label={label}>
      {AVATARS.map((a) => {
        const selected = value === a.id;
        return (
          <button
            key={a.id}
            type="button"
            className="avpick__opt"
            role="radio"
            aria-checked={selected}
            aria-label={a.label}
            title={a.label}
            onClick={() => onChange?.(a.id)}
          >
            <span aria-hidden="true">{a.emoji}</span>
          </button>
        );
      })}
    </div>
  );
}

injectStyles(
  "maths-avatarpicker-styles",
  `
.avatar-bubble{display:inline-grid;place-items:center;border-radius:50%;
  background:color-mix(in srgb,var(--brand) 10%,#fff);border:1px solid var(--line);line-height:1;flex:0 0 auto;}
.avpick{display:grid;grid-template-columns:repeat(auto-fill,minmax(56px,1fr));gap:.5rem;}
.avpick__opt{min-height:56px;min-width:56px;font-size:1.7rem;line-height:1;border-radius:14px;
  border:2px solid var(--line);background:var(--card);display:grid;place-items:center;cursor:pointer;
  transition:transform .04s ease;}
.avpick__opt:active{transform:translateY(1px);}
.avpick__opt:focus-visible{outline:2px solid var(--sky);outline-offset:2px;}
.avpick__opt[aria-checked="true"]{border-color:var(--brand);
  background:color-mix(in srgb,var(--brand) 12%,#fff);
  box-shadow:0 0 0 3px color-mix(in srgb,var(--brand) 22%,#fff);}
`
);
